import { useState } from 'react';
import type { ReactNode } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Header } from '../../components/layout/Header';
import { useAdmin } from '../../context/admin';
import type { Access } from '../../lib/access';
import { ADMIN_SIMULATED, resetAdminStore } from '../../lib/api/admin';
import { SkeletonTable } from '../../components/ui/Skeleton';

/**
 * The admin area's pages, in the order somebody setting up a company walks
 * through them: the company, its wells, what each role may do, what each role
 * sees, and finally the people.
 */
const TABS = [
  { key: 'Companies', label: 'Companies', needs: 'structure' },
  { key: 'Wells', label: 'Wells', needs: 'structure' },
  { key: 'Roles', label: 'Roles', needs: 'structure' },
  { key: 'Layouts', label: 'Layouts', needs: 'structure' },
  { key: 'Users', label: 'Users', needs: 'users' },
  // Pages with no tab of their own. The audit log is reached from Users (one
  // person's, or every user's) and lights Users up while it is open;
  // customization is reached from the user menu and shows no tabs at all.
  { key: 'AuditLogs', label: 'Audit Logs', needs: 'structure', under: 'Users' },
  { key: 'Customization', label: 'Customization', needs: 'structure', alone: true },
] as const;

export type AdminTab = (typeof TABS)[number]['key'];

const allowedTabs = (access: Access) => TABS.filter((t) => access.admin[t.needs]);

/** /web/Admin: on to the first page this person may open, or out. */
export function AdminIndex() {
  const { access } = useAdmin();
  const first = allowedTabs(access)[0];
  return <Navigate to={first ? `/web/Admin/${first.key}` : '/web'} replace />;
}

interface Props {
  active: AdminTab;
  /** The page's heading: "Well Management". */
  title: string;
  /** What sits in the pill beside it: "2 Wells". */
  count?: ReactNode;
  /** The page's one main button, at the right of the heading. */
  action?: ReactNode;
  children: ReactNode;
}

/**
 * The frame every admin page sits in. It is the frame of Company Management
 * (pages/CompaniesHome), so moving between the tabs changes the list and
 * nothing around it: heading and count, the main button, the tabs.
 */
export function AdminShell({ active, title, count, action, children }: Props) {
  const { access, commit, previewAs, storeLoaded, wellsError } = useAdmin();
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);

  const tabs = allowedTabs(access);
  const current = tabs.find((t) => t.key === active);

  // The API will refuse anyone else; hiding the page is the courtesy.
  if (!current) return <AdminIndex />;

  async function clearAll() {
    setBusy(true);
    try {
      previewAs(null);
      await commit(resetAdminStore());
    } finally {
      setBusy(false);
      setAsking(false);
    }
  }

  return (
    <div className="app">
      <Header context="wells" />

      <main className="cmpage">
        <header className="cmhead">
          <h1>{title}</h1>
          {count != null && <span className="cmcount">{count}</span>}
          <span className="cmspacer" />
          {action}
        </header>

        {!('alone' in current) && (
          <nav className="cmtabs" aria-label="Administration">
            {tabs
              .filter((t) => !('under' in t) && !('alone' in t))
              .map((t) =>
                t.key === active ? (
                  <span key={t.key} className="cmtab is-active" aria-current="page">
                    {t.label}
                  </span>
                ) : (
                  <Link
                    key={t.key}
                    to={`/web/Admin/${t.key}`}
                    className={'under' in current && current.under === t.key ? 'cmtab is-active' : 'cmtab'}
                  >
                    {t.label}
                  </Link>
                ),
              )}
          </nav>
        )}

        {/* Until the server has answered there is nothing to show but empty
            lists, and an empty list reads as "nothing exists". */}
        {!storeLoaded && <SkeletonTable rows={6} cols={5} />}
        {/* The Wells page says so itself, with what that means for its list. */}
        {storeLoaded && wellsError && active !== 'Wells' && (
          <div className="alert" role="alert">
            {wellsError}
          </div>
        )}
        {storeLoaded && children}

        {ADMIN_SIMULATED && (
          <p className="cmsim">
            Simulated: companies, roles, layouts and users are kept in this browser only, and a user created here
            cannot sign in yet. They move to the server once the API has these endpoints.{' '}
            {access.kind === 'superadmin' &&
              (asking ? (
                <>
                  Throw away everything on these pages?{' '}
                  <button type="button" className="cmbtn cmbtn--small cmbtn--danger" onClick={() => void clearAll()} disabled={busy}>
                    Clear
                  </button>{' '}
                  <button type="button" className="cmbtn cmbtn--small" onClick={() => setAsking(false)}>
                    Keep
                  </button>
                </>
              ) : (
                <button type="button" className="adminlink" onClick={() => setAsking(true)}>
                  Clear the simulated data
                </button>
              ))}
          </p>
        )}
      </main>
    </div>
  );
}

/**
 * The error and the confirmation, where every admin page shows them. While a
 * dialog is open the error is shown inside it instead, next to what caused it.
 */
export function ActionMessages({
  error,
  notice,
  inDialog = false,
}: {
  error: string | null;
  notice: string | null;
  inDialog?: boolean;
}) {
  return (
    <>
      {error && !inDialog && (
        <div className="alert cmmsg" role="alert">
          {error}
        </div>
      )}
      {notice && !error && (
        <p className="kbnotice cmmsg" role="status">
          {notice}
        </p>
      )}
    </>
  );
}

/** "Delete" in a row of the list; it asks once before it does. */
export function ConfirmDelete({
  question,
  busy,
  onDelete,
}: {
  question: string;
  busy: boolean;
  onDelete: () => void;
}) {
  const [asking, setAsking] = useState(false);

  if (!asking) {
    return (
      <button type="button" className="cmbtn cmbtn--small cmbtn--danger" onClick={() => setAsking(true)} disabled={busy}>
        Delete
      </button>
    );
  }

  return (
    <>
      <span className="cmconfirm">{question}</span>
      <button type="button" className="cmbtn cmbtn--small cmbtn--danger" onClick={onDelete} disabled={busy}>
        Delete
      </button>
      <button type="button" className="cmbtn cmbtn--small" onClick={() => setAsking(false)}>
        Keep
      </button>
    </>
  );
}
